import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { useProjects } from "~/entities/project/api";
import { useScenesByProject } from "~/entities/scene/api";
import { useUserWorkspaces } from "~/entities/workspace/api";
import { Button } from "~/shared/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/shared/ui/dialog";
import { Label } from "~/shared/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/shared/ui/select";

import { useMoveChat } from "~/entities/chat/chat-mutations";

// Radix Select forbids empty-string item values, so "no project" / "no scene" use a sentinel.
const NONE = "__none__";

interface MoveChatDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    chatId: string;
    // Current attachment of the chat — used as the initial form state.
    workspaceId: string;
    projectId?: string | null;
    sceneId?: string | null;
}

export function MoveChatDialog({
    open,
    onOpenChange,
    chatId,
    workspaceId,
    projectId,
    sceneId,
}: MoveChatDialogProps) {
    const { t } = useTranslation("chat");
    const move = useMoveChat();

    const [wsId, setWsId] = useState<string>(workspaceId);
    const [projectValue, setProjectValue] = useState<string>(projectId ?? NONE);
    const [sceneValue, setSceneValue] = useState<string>(sceneId ?? NONE);

    useEffect(() => {
        if (open) {
            setWsId(workspaceId);
            setProjectValue(projectId ?? NONE);
            setSceneValue(sceneId ?? NONE);
            move.reset();
        }
        // `move` is a stable mutation object; intentionally not a dep.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, workspaceId, projectId, sceneId]);

    const workspacesQuery = useUserWorkspaces();
    const projectsQuery = useProjects({ tab: "all", workspaceId: wsId }, { enabled: open });
    const scenesQuery = useScenesByProject(projectValue === NONE ? undefined : projectValue);

    const workspaces = workspacesQuery.data ?? [];
    const projects = projectsQuery.data?.items ?? [];
    const scenes = scenesQuery.data?.data ?? [];

    const handleSubmit = async () => {
        if (move.isPending) return;
        try {
            await move.mutateAsync({
                chatId,
                workspaceId,
                targetWorkspaceId: wsId,
                targetProjectId: projectValue === NONE ? undefined : projectValue,
                targetSceneId: projectValue === NONE || sceneValue === NONE ? undefined : sceneValue,
            });
            onOpenChange(false);
        } catch (error) {
            const fallback = t("header.moveDialog.errorGeneric");
            toast.error(error instanceof Error && error.message ? error.message : fallback);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t("header.moveDialog.title")}</DialogTitle>
                    <DialogDescription>{t("header.moveDialog.description")}</DialogDescription>
                </DialogHeader>

                <div className="grid gap-4">
                    <div className="grid gap-2">
                        <Label>{t("header.moveDialog.workspaceLabel")}</Label>
                        <Select
                            value={wsId}
                            onValueChange={(v) => {
                                setWsId(v);
                                setProjectValue(NONE);
                                setSceneValue(NONE);
                            }}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder={t("header.moveDialog.workspacePlaceholder")} />
                            </SelectTrigger>
                            <SelectContent position="popper" align="start">
                                {workspaces.map((w) => (
                                    <SelectItem key={w.id} value={w.id}>
                                        {w.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid gap-2">
                        <Label>{t("header.moveDialog.projectLabel")}</Label>
                        <Select
                            value={projectValue}
                            onValueChange={(v) => {
                                setProjectValue(v);
                                setSceneValue(NONE);
                            }}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder={t("header.moveDialog.projectPlaceholder")} />
                            </SelectTrigger>
                            <SelectContent position="popper" align="start">
                                <SelectItem value={NONE}>{t("header.moveDialog.noProject")}</SelectItem>
                                {projects.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>
                                        {p.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid gap-2">
                        <Label>{t("header.moveDialog.sceneLabel")}</Label>
                        <Select
                            value={sceneValue}
                            onValueChange={setSceneValue}
                            disabled={projectValue === NONE}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder={t("header.moveDialog.scenePlaceholder")} />
                            </SelectTrigger>
                            <SelectContent position="popper" align="start">
                                <SelectItem value={NONE}>{t("header.moveDialog.noScene")}</SelectItem>
                                {scenes.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>
                                        {s.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={move.isPending}
                    >
                        {t("header.moveDialog.cancel")}
                    </Button>
                    <Button type="button" variant="accent" onClick={handleSubmit} disabled={move.isPending}>
                        {move.isPending ? t("header.moveDialog.submitting") : t("header.moveDialog.move")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
